import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { COUNTRIES } from "@/components/home/data";
import { siteTitleInline } from "@/components/home/SiteTitle";

const inputCls = "w-full mt-2 bg-transparent border-b border-[#1A2E26]/20 py-2.5 outline-none focus:border-[#C86A51] transition-colors placeholder:text-[#1A2E26]/40";

export default function WhatsAppUpdates() {
  const [content, setContent] = useState(null);
  const [form, setForm] = useState({ name: "", number: "", program: "", batch: "", place: "" });
  const [dialCode, setDialCode] = useState("+91");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState("");

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      setContent(data);
      setForm((f) => ({ ...f, program: data?.programs?.[0]?.name || "" }));
    }).catch((err) => console.warn("Could not load content", err));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const digits = form.number.replace(/\D/g, "");
    if (!form.name.trim()) return toast.error("Please enter your name");
    if (digits.length < 6) return toast.error("Please enter a valid WhatsApp number");
    setSending(true);
    try {
      const { data } = await api.post("/whatsapp-subscribe", { name: form.name.trim(), whatsapp: `${dialCode}${digits}`, program: form.program, batch: form.batch, place: form.place });
      setDone(data.message || "Thank you!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your number. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const closed = content && content.whatsapp_form_visible === false;

  return (
    <main data-testid="whatsapp-page" className="relative min-h-screen overflow-hidden bg-[#F4F1EA] text-[#1A2E26]">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F4F1EA] via-[#EDECE3] to-[#E4EAE0]" />
      <div className="absolute -z-10 top-[-12%] right-[-8%] w-[48vw] h-[48vw] rounded-full bg-[#25D366]/15 blur-[120px]" aria-hidden="true" />
      <div className="mx-auto max-w-2xl px-6 py-8 md:py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Link to="/" data-testid="whatsapp-back-home" className="inline-flex items-center gap-2 rounded-full border border-[#1A2E26]/15 bg-white/60 px-5 py-2.5 text-sm backdrop-blur transition-colors duration-300 hover:bg-[#1A2E26] hover:text-white">
            <ArrowLeft size={16} /> Back to Home
          </Link>
          <span className="font-serif text-xl sm:text-2xl tracking-tight">{siteTitleInline(content)}</span>
        </div>

        <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-14 rounded-3xl border border-white/70 bg-white/80 backdrop-blur shadow-[0_8px_40px_rgba(26,46,38,0.08)] p-7 md:p-10">
          <div className="flex items-center gap-3 text-[#128C7E]">
            <span className="grid place-items-center w-11 h-11 rounded-full bg-[#25D366]/15"><MessageCircle size={22} /></span>
            <p className="text-xs uppercase tracking-[0.3em]">WhatsApp updates</p>
          </div>
          <h1 data-testid="whatsapp-form-title" className="mt-5 font-serif font-light text-4xl sm:text-5xl tracking-tight leading-[1.05]">{content?.whatsapp_form_title || "Get updates on WhatsApp"}</h1>
          <p data-testid="whatsapp-form-intro" className="mt-4 text-base leading-relaxed text-[#1A2E26]/75 whitespace-pre-line">{content?.whatsapp_form_intro || "Share your WhatsApp number to receive Zoom links, batch announcements and reminders."}</p>

          {closed ? (
            <p data-testid="whatsapp-form-closed" className="mt-8 rounded-2xl bg-[#F4F1EA] p-5 text-sm">This form is currently closed. Please check back later.</p>
          ) : done ? (
            <div data-testid="whatsapp-form-success" className="mt-8 rounded-2xl bg-[#25D366]/10 border border-[#25D366]/30 p-6 flex items-start gap-3">
              <CheckCircle2 className="text-[#128C7E] shrink-0" />
              <p className="text-base leading-relaxed">{done}</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 grid gap-6" data-testid="whatsapp-form">
              <div>
                <label className="text-xs uppercase tracking-widest text-[#1A2E26]/60">Full name</label>
                <input required data-testid="wa-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#1A2E26]/60">WhatsApp number</label>
                <div className="mt-2 flex gap-2">
                  <select data-testid="wa-form-country" value={dialCode} onChange={(e) => setDialCode(e.target.value)} className="bg-transparent border-b border-[#1A2E26]/20 py-2.5 pr-1 outline-none focus:border-[#C86A51] text-sm shrink-0">
                    {COUNTRIES.map((c) => <option key={c.code + c.name} value={c.code}>{c.flag} {c.code}</option>)}
                  </select>
                  <input required type="tel" inputMode="numeric" data-testid="wa-form-number" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value.replace(/\D/g, "") })} maxLength={15} placeholder="WhatsApp number" className="w-full bg-transparent border-b border-[#1A2E26]/20 py-2.5 outline-none focus:border-[#C86A51] transition-colors placeholder:text-[#1A2E26]/40" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#1A2E26]/60">Program</label>
                  <select data-testid="wa-form-program" value={form.program} onChange={(e) => setForm({ ...form, program: e.target.value })} className={inputCls}>
                    {(content?.programs || []).map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
                    <option value="Other">Other / Not sure</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#1A2E26]/60">Batch number (if known)</label>
                  <input data-testid="wa-form-batch" value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })} placeholder="e.g. 168" className={inputCls} />
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-[#1A2E26]/60">Place (optional)</label>
                <input data-testid="wa-form-place" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })} placeholder="City / Town" className={inputCls} />
              </div>
              <button type="submit" disabled={sending} data-testid="wa-form-submit" className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[#128C7E] text-white px-8 py-3.5 text-sm font-medium hover:bg-[#0f7568] transition-colors disabled:opacity-60">
                <MessageCircle size={16} /> {sending ? "Saving…" : "Save my WhatsApp number"}
              </button>
              <p className="text-xs text-[#1A2E26]/55">We only use this number to send Positive Life programme updates. No spam.</p>
            </form>
          )}
        </motion.section>
      </div>
    </main>
  );
}
